import { useEffect, useState } from "react";

// The app pins every MUI modal to 9998 (see `.MuiModal-root` in App.css) and
// floats dropdowns/confirms just above at 9999. We keep that as the floor: the
// first/only modal still lands on 9998, so single-modal screens are unchanged.
// Each modal opened on top climbs by STEP, and floating layers (the custom
// Popper, the confirm dialog) follow the topmost modal via useTopZIndex.
const BASE = 9998;
const STEP = 10;

// Active modal z-indices, lowest → highest in open order. Module-level so every
// modal shares one stack regardless of how it renders — the MUI-based
// CustomModal/ConfirmModal and the portal-based FormBuilderModal included.
let active = [];
const subscribers = new Set();

const notify = () => subscribers.forEach((cb) => cb());

function acquire() {
  const z = active.length ? active[active.length - 1] + STEP : BASE;
  active.push(z);
  notify();
  return z;
}

function release(z) {
  active = active.filter((v) => v !== z);
  notify();
}

const peekTop = () => (active.length ? active[active.length - 1] : null);
const isTopmost = (z) => active.length === 0 || active[active.length - 1] === z;

/**
 * Stacking manager for overlapping modals. When `open` flips true the modal
 * acquires a z-index above any already open and releases it on close, so a
 * modal launched from inside another (the form builder opened from the program
 * editor, say) sits above its parent instead of tying with it.
 *
 * The returned `zIndex` is the modal's top visual layer — for a backdrop+shell
 * modal, render the backdrop at `zIndex - 1` and the shell at `zIndex`.
 *
 * `isTop` is false while another modal sits above, so a parent MUI modal can
 * drop its focus trap and let the modal on top receive keyboard input.
 */
export default function useModalZIndex(open) {
  // Keep the last acquired value after close so exit animations don't drop
  // behind a sibling mid-transition.
  const [zIndex, setZIndex] = useState(BASE);
  const [isTop, setIsTop] = useState(true);

  useEffect(() => {
    if (!open) return undefined;

    const z = acquire();
    setZIndex(z);

    const sync = () => setIsTop(isTopmost(z));
    subscribers.add(sync);
    sync();

    return () => {
      subscribers.delete(sync);
      release(z);
    };
  }, [open]);

  return { zIndex, isTop };
}

/**
 * For floating layers that must sit just above whatever modal is on top
 * (dropdown poppers, the confirm dialog). Returns the z-index to use; falls
 * back to the historical 9999 when no modal is open.
 */
export function useTopModalZIndex() {
  const [top, setTop] = useState(peekTop);

  useEffect(() => {
    const sync = () => setTop(peekTop());
    subscribers.add(sync);
    sync();
    return () => subscribers.delete(sync);
  }, []);

  return top != null ? top + 1 : BASE + 1;
}
